import sampleBoards from '../../../../contracts/native-sportsbook/v1/player-prop-boards.json' with { type: 'json' }

const MILESTONE_PREFIX_PATTERN = /^\d+\+\s*/

// Mirrors the native apps and the runtime: a column names a provider criterion
// and outcome type (and optionally one line); rows are the players the
// provider names on those outcomes.

export const PLAYER_PROP_SELECTIONS = [
  { value: 'participant', label: 'Player list (first to occur)' },
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
  { value: 'over', label: 'Over' },
  { value: 'under', label: 'Under' },
]

export const PLAYER_PROP_MODES = [
  { value: 'competition', label: "League's nearest games" },
  { value: 'event', label: 'One game' },
  { value: 'team', label: "A team's next game" },
]

// Leagues the provider templates below were observed in. Studio adds any
// league the current inventory knows.
export const PLAYER_PROP_LEAGUES = [
  { sport: 'american_football', competition: 'NFL' },
  { sport: 'baseball', competition: 'MLB' },
  { sport: 'basketball', competition: 'NBA' },
  { sport: 'ice_hockey', competition: 'NHL' },
  { sport: 'soccer', competition: 'Premier League' },
]

// Provider criteria observed on the Kambi production Offering on 2026-10-06.
// Discovery under live data replaces these with whatever the games post.
export const PLAYER_PROP_TEMPLATES = [
  { key: 'nfl_touchdowns', sport: 'american_football', competition: 'NFL', name: 'Touchdowns: first, anytime, 2+', title: 'Player Touchdowns', rank: 1, columns: [
    { label: 'FIRST', criterion_id: '1001488021', selection: 'participant' },
    { label: 'ANYTIME', criterion_id: '1002231004', selection: 'yes', line_millis: 1000 },
    { label: '2+', criterion_id: '1006316175', selection: 'yes', line_millis: 2000 },
  ] },
  { key: 'nfl_passing', sport: 'american_football', competition: 'NFL', name: 'Passing yards over/under', title: 'Passing Yards', rank: 0, columns: [
    { label: 'OVER', criterion_id: '1001159738', selection: 'over' },
    { label: 'UNDER', criterion_id: '1001159738', selection: 'under' },
  ] },
  { key: 'nfl_rushing', sport: 'american_football', competition: 'NFL', name: 'Rushing yards over/under', title: 'Rushing Yards', rank: 0, columns: [
    { label: 'OVER', criterion_id: '1004952424', selection: 'over' },
    { label: 'UNDER', criterion_id: '1004952424', selection: 'under' },
  ] },
  { key: 'nfl_receiving', sport: 'american_football', competition: 'NFL', name: 'Receiving yards over/under', title: 'Receiving Yards', rank: 0, columns: [
    { label: 'OVER', criterion_id: '1004952425', selection: 'over' },
    { label: 'UNDER', criterion_id: '1004952425', selection: 'under' },
  ] },
  { key: 'mlb_hitters', sport: 'baseball', competition: 'MLB', name: 'Home run, hits over/under', title: 'Hitter Props', rank: 0, columns: [
    { label: 'HR', criterion_id: '1004919588', selection: 'yes' },
    { label: 'HITS O', criterion_id: '1004988034', selection: 'over' },
    { label: 'HITS U', criterion_id: '1004988034', selection: 'under' },
  ] },
  { key: 'nhl_goals', sport: 'ice_hockey', competition: 'NHL', name: 'Goals: first, anytime, 2+', title: 'Goal Scorers', rank: 1, columns: [
    { label: 'FIRST', criterion_id: '1007622945', selection: 'participant' },
    { label: 'ANYTIME', criterion_id: '1007622953', selection: 'yes', line_millis: 1000 },
    { label: '2+', criterion_id: '1007622961', selection: 'yes', line_millis: 2000 },
  ] },
  { key: 'epl_goals', sport: 'soccer', competition: 'Premier League', name: 'Goals: first, anytime, 2+', title: 'Goal Scorers', rank: 1, columns: [
    { label: 'FIRST', criterion_id: '1005153918', selection: 'participant' },
    { label: 'ANYTIME', criterion_id: '1001159886', selection: 'yes', line_millis: 1000 },
    { label: '2+', criterion_id: '1005153925', selection: 'yes', line_millis: 2000 },
  ] },
]

const newId = () => globalThis.crypto.randomUUID()

export function templatesFor(sport, competition) {
  return PLAYER_PROP_TEMPLATES.filter((template) => template.sport === sport && template.competition.toLowerCase() === (competition || '').toLowerCase())
}

/** Columns and rank for a template or suggestion, with fresh column identities. */
export function columnsFromTemplate(template, createUUID = newId) {
  const columns = template.columns.map((column) => ({ id: createUUID(), ...column }))
  return { columns, rank_column_id: columns[template.rank ?? 0].id }
}

export function columnParam(column) {
  return `${column.criterion_id}:${column.selection}${column.line_millis != null ? `:${column.line_millis}` : ''}`
}

export function rankIndex(component) {
  return Math.max(0, (component.columns || []).findIndex((column) => column.id === component.rank_column_id))
}

/** The runtime board request a component makes, in the same shape the native apps send. */
export function boardQuery(component) {
  const scope = component.scope || {}
  const query = { sport: scope.sport, competition: scope.competition, mode: scope.mode, columns: (component.columns || []).map(columnParam).join(','), rank: rankIndex(component) }
  if (scope.mode === 'event') query.event_id = scope.event_id
  if (scope.mode === 'team') query.team_id = scope.team?.participant_id
  return query
}

export function boardQueryComplete(query) {
  return Boolean(query.sport && query.competition && query.columns && (query.mode !== 'event' || query.event_id) && (query.mode !== 'team' || query.team_id))
}

/**
 * Sample games answer only their canonical requests, as the apps' fixture repository does,
 * except that a different rank column re-orders the canonical rows the way the runtime ranks.
 */
export function sampleBoard(query) {
  const entries = sampleBoards.boards.filter(({ request }) => request.sport === query.sport &&
    request.competition.toLowerCase() === (query.competition || '').toLowerCase() &&
    request.mode === query.mode && (request.event_id || undefined) === (query.event_id || undefined) &&
    (request.team_id || undefined) === (query.team_id || undefined) && request.columns === query.columns)
  const exact = entries.find(({ request }) => request.rank === query.rank)
  if (exact) return exact.board
  const board = entries[0]?.board
  return board ? { ...board, rows: rankedRows(board.rows, board.columns, query.rank) } : null
}

const cellOpen = (cell) => cell.status === 'OPEN' && cell.market_status === 'OPEN'
const byteOrder = (left, right) => (left < right ? -1 : left > right ? 1 : 0)

/**
 * Rows in the runtime's rank order: players without a price in the rank column drop out, then
 * open prices first, the highest line for over/under, the shortest price, then name and id.
 */
export function rankedRows(rows, columns, rank) {
  const byLine = columns[rank]?.selection === 'over' || columns[rank]?.selection === 'under'
  return rows.filter((row) => row.cells[rank]).toSorted((left, right) => {
    const a = left.cells[rank]
    const b = right.cells[rank]
    if (cellOpen(a) !== cellOpen(b)) return cellOpen(a) ? -1 : 1
    if (byLine && (a.line_millis ?? -1) !== (b.line_millis ?? -1)) return (b.line_millis ?? -1) - (a.line_millis ?? -1)
    if ((a.odds_millis ?? Infinity) !== (b.odds_millis ?? Infinity)) return (a.odds_millis ?? Infinity) - (b.odds_millis ?? Infinity)
    return byteOrder(left.player.name, right.player.name) || byteOrder(left.player.id, right.player.id)
  })
}

/** Provider market names the sample boards carry, by criterion id; first named column wins. */
const SAMPLE_MARKET_NAMES = new Map()
for (const { board } of sampleBoards.boards) {
  for (const column of board.columns) {
    const name = column.market_short_label || column.market_label
    if (name && !SAMPLE_MARKET_NAMES.has(column.criterion_id)) SAMPLE_MARKET_NAMES.set(column.criterion_id, name)
  }
}

/**
 * What a column's Market shows before discovery has listed the posted markets:
 * the provider's own name for the criterion where a sample board carries one,
 * else the template that uses it.
 */
export function knownMarketLabel(criterionId) {
  const name = SAMPLE_MARKET_NAMES.get(criterionId)
  if (name) return `${name} (${criterionId})`
  const template = PLAYER_PROP_TEMPLATES.find((candidate) => candidate.columns.some((column) => column.criterion_id === criterionId))
  return template ? `${template.name} (${criterionId})` : `Provider market ${criterionId}`
}

/** Events and teams the sample boards know for a league, for scope pickers without a runtime. */
export function sampleDirectory(sport, competition) {
  const events = new Map()
  for (const { request, board } of sampleBoards.boards) {
    if (request.sport !== sport || request.competition.toLowerCase() !== (competition || '').toLowerCase()) continue
    for (const event of board.events) events.set(event.event_id, event)
  }
  const teams = new Map()
  for (const event of events.values()) for (const team of event.participants) teams.set(team.id, { id: team.id, name: team.name })
  return { events: [...events.values()], teams: [...teams.values()] }
}

/** A copied board gets fresh column identities; the rank keeps pointing at the same column. */
export function reidentifyColumns(component, uuid) {
  if (!Array.isArray(component.columns)) return
  const identities = new Map(component.columns.map((column) => [column.id, uuid()]))
  for (const column of component.columns) column.id = identities.get(column.id)
  if (component.rank_column_id) component.rank_column_id = identities.get(component.rank_column_id) ?? component.columns[0]?.id
}

/** Whether a board's league is the hub's league, compared as the server's hub check does. */
export function boardFitsHub(scope, context) {
  return scope?.sport === context.sport && scope?.competition?.toLowerCase() === context.competition?.toLowerCase()
}

const columnKey = (column) => `${column.criterion_id}:${column.selection}:${column.line_millis ?? ''}`

/**
 * The column Add column appends: a posted market, pick and line the board does
 * not show yet, trying markets the board does not use first. Null when every
 * combination is on the board, because the server rejects repeated columns.
 */
export function nextPlayerPropColumn(columns, markets) {
  const used = new Set(columns.map(columnKey))
  const criteria = new Set(columns.map((column) => column.criterion_id))
  const ordered = [...markets.filter((market) => !criteria.has(market.criterion_id)), ...markets.filter((market) => criteria.has(market.criterion_id))]
  for (const market of ordered) {
    for (const selection of market.selections) {
      const posted = market.lines.map((line) => line.line_millis)
      // Over/under defaults to each player's main line; yes/no names a posted line.
      const lines = selection === 'participant' ? [undefined] : selection === 'over' || selection === 'under' ? [undefined, ...posted] : posted.length ? posted : [undefined]
      for (const line of lines) {
        const column = { criterion_id: market.criterion_id, selection, ...(line === undefined ? {} : { line_millis: line }) }
        if (used.has(columnKey(column))) continue
        const label = selection === 'over' || selection === 'under' ? selection : market.short_label || 'new'
        return { label: label.slice(0, 12).toUpperCase(), ...column }
      }
    }
  }
  return null
}

export function lineText(millis) {
  return String(Math.abs(millis) / 1000)
}

/** The selection label the apps put on the betslip: player, provider market and side. */
export function selectionLabel(row, column, authoredLabel, cell) {
  const market = column.market_short_label || column.market_label || authoredLabel
  const line = cell.line_millis != null ? ` ${lineText(cell.line_millis)}` : ''
  const suffix = { over: ` Over${line}`, under: ` Under${line}`, no: ' No' }[column.selection] || ''
  return `${row.player.name} · ${market}${suffix}`
}

function ladderLabel(line) {
  return `${lineText(line.line_millis)}+`
}

/**
 * Suggestions built only from markets the selected games post: first / anytime
 * / 2+ for every occurrence type with a first-occurrence player list, over/under
 * pairs, and three-rung ladders. A first column needs the provider's
 * occurrence number 1, never a label, so a last-scorer list cannot stand in for
 * it. Suggestions that repeat one of [templates] or each other are dropped.
 */
export function suggestionsFromMarkets(markets = [], templates = []) {
  const suggestions = []
  for (const list of markets.filter((market) => market.offer_shape === 'player_list' && market.selections.includes('participant') && market.occurrence_type && market.occurrence_number === 1)) {
    const family = markets.filter((market) => market.offer_shape === 'player_line' && market.occurrence_type === list.occurrence_type && market.lifetime === list.lifetime && market.selections.includes('yes'))
    const withLine = (line) => family.find((market) => market.lines.some((candidate) => candidate.line_millis === line))
    const anytime = withLine(1000)
    const twoPlus = withLine(2000)
    if (!anytime) continue
    const columns = [{ label: 'FIRST', criterion_id: list.criterion_id, selection: 'participant' }, { label: 'ANYTIME', criterion_id: anytime.criterion_id, selection: 'yes', line_millis: 1000 }]
    if (twoPlus) columns.push({ label: '2+', criterion_id: twoPlus.criterion_id, selection: 'yes', line_millis: 2000 })
    const noun = list.occurrence_type.charAt(0) + list.occurrence_type.slice(1).toLowerCase()
    suggestions.push({ key: `scorer:${list.criterion_id}`, name: `${noun}: first, anytime${twoPlus ? ', 2+' : ''}`, title: `Player ${noun}`, rank: 1, columns })
  }
  for (const market of markets) {
    const name = market.short_label || market.label
    if (market.selections.includes('over') && market.selections.includes('under')) {
      suggestions.push({ key: `ou:${market.criterion_id}`, name: `${name} over/under`, title: name.slice(0, 80), rank: 0, columns: [
        { label: 'OVER', criterion_id: market.criterion_id, selection: 'over' },
        { label: 'UNDER', criterion_id: market.criterion_id, selection: 'under' },
      ] })
    }
    if (market.selections.includes('yes') && market.lines.length >= 3 && market.offer_shape === 'player_line') {
      const rungs = [market.lines[0], market.lines[Math.floor(market.lines.length / 2)], market.lines.at(-1)]
      suggestions.push({ key: `ladder:${market.criterion_id}`, name: `${name.replace(MILESTONE_PREFIX_PATTERN, '')} milestones`, title: name.replace(MILESTONE_PREFIX_PATTERN, '').slice(0, 80), rank: 0, columns: rungs.map((line) => ({ label: ladderLabel(line), criterion_id: market.criterion_id, selection: 'yes', line_millis: line.line_millis })) })
    }
  }
  const offered = new Set(templates.map((template) => columnsKey(template.columns)))
  return suggestions.filter((suggestion) => {
    const key = columnsKey(suggestion.columns)
    if (offered.has(key)) return false
    offered.add(key)
    return true
  })
}

/** Two boards ask the same thing when their columns name the same markets, picks and lines in order. */
const columnsKey = (columns) => columns.map(columnKey).join(',')

/** The label a market option shows: the provider's label, with its short label when posted. */
export function marketOptionLabel(market) {
  const shape = market.offer_shape === 'player_list' ? 'player list' : `${market.player_count} players`
  return `${market.short_label || market.label} · ${shape}`
}
